// SPDX-License-Identifier: BUSL-1.1

/**
 * Operator activity drill-down (CEO-V4 L3).
 *
 * Quick-glance side sheet opened from a row click on the `/operators`
 * fleet grid. Shows two parallel views of one agent's activity stream:
 *
 *   1. Live   — wraps the existing <LiveStreamPanel> against the agent's
 *               most-recent active session (per-session SSE pattern).
 *               If no recent session exists, surfaces a "no live stream"
 *               message rather than opening a stream-less connection.
 *   2. History — paginated list of historical activity events from
 *               GET /api/agents/:agentId/activity?limit=50&before=<cursor>.
 *               Uses cursor-based pagination via the `nextCursor` field
 *               returned by the endpoint.
 *
 * Scope discipline (CEO-V4 L3):
 *   - Composition-only. Reuses event-columns, LiveStreamPanel,
 *     DataTable, badges, and the auth-aware apiFetch — does NOT
 *     rebuild SSE plumbing or activity rendering.
 *   - Does NOT introduce a new fleet-SSE dependency. L2's fleet stream
 *     (P2-SSE) is in-flight in parallel; this lane works without it.
 *     If it ships, a follow-up can swap the per-session subscription
 *     for a fleet stream filtered by agentId.
 *   - Does NOT mutate the `/agents/:id` deep route — that's still the
 *     full agent detail. This sheet is the lighter-weight surface
 *     reachable from the operators fleet view.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { apiFetch } from '@/lib/api-client';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table';
import { LiveStreamPanel } from '@/components/live-stream-panel';
import { AgentHealthBadge } from '@/components/badges';
import { deriveAgentHealth } from '@/lib/agent-health';
import { eventColumns } from '@/components/agent-detail/event-columns';
import { AgentFleetActivity } from '@/components/agent-fleet-activity';
import type {
  ActivityEvent,
  ActivityEventKind,
} from '@/components/agent-detail/types';

/* ── Types ───────────────────────────────────────────────────────────────── */

interface DrilldownAgent {
  agentId: string;
  name: string;
  status: 'idle' | 'running' | 'stopped' | 'error';
  lastHeartbeat: string | null;
  /**
   * NocoDB #4789. LiveSection branches on `livePerSessionStream`:
   *   - true  → agent emits to a per-session SSE; use <LiveStreamPanel>
   *   - false → no per-session SSE; use agent-filtered fleet stream
   *   - undefined → fall back to inferring from work-unit `kind` so
   *     legacy agents without the explicit flag still work
   */
  capabilities?: {
    sessions?: boolean;
    jobs?: boolean;
    livePerSessionStream?: boolean;
  };
}

interface ActivityResponse {
  events: ActivityEvent[];
  nextCursor: string | null;
}

/**
 * Subset of the WorkUnit shape we consume from /api/agents/:id/work-units.
 * Mirrors `packages/core/src/worker/lib/work-unit.ts` — keep in sync if
 * the server adds fields.
 */
interface WorkUnit {
  id: string;
  agentId: string;
  kind: 'session' | 'job';
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  costUsd: number | null;
  sdkNativeId: string | null;
}

interface WorkUnitsResponse {
  data: WorkUnit[];
  total: number;
  sources: { sessions: number; jobs: number };
}

/* ── Empty / loading sub-views ──────────────────────────────────────────── */

function HistorySkeleton() {
  // Three placeholder rows so the layout doesn't jump when results arrive.
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading history">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-10 animate-pulse rounded-md bg-neutral-800/60"
        />
      ))}
    </div>
  );
}

function NoLiveStream({ reason }: { reason: string }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/50 p-4 text-xs text-neutral-400">
      <p className="font-medium text-neutral-300">No live stream available</p>
      <p className="mt-1 text-neutral-500">{reason}</p>
    </div>
  );
}

/* ── Live section (SDK-agnostic via /work-units + capability) ──────────── */

/**
 * Pick the freshest "active or recent" work unit as the live-stream
 * subject. Preference order:
 *   1. A running work-unit (endedAt is null, status NOT terminal)
 *   2. The most recently started one of any kind/status
 *
 * Both session and job rows qualify — Layer B's job is to decide which
 * LIVE-STREAM COMPONENT to mount based on the agent's capabilities +
 * the picked unit's kind, not to filter by kind here.
 */
function pickStreamUnit(units: WorkUnit[] | undefined): WorkUnit | null {
  if (!units || units.length === 0) return null;
  const running = units.find((u) => u.endedAt === null);
  if (running) return running;
  return [...units].sort((a, b) => {
    const aT = a.startedAt ? Date.parse(a.startedAt) : 0;
    const bT = b.startedAt ? Date.parse(b.startedAt) : 0;
    return bT - aT;
  })[0];
}

/**
 * Decide which live-stream component to render. Capability-driven per
 * NocoDB #4789:
 *
 *   - Explicit `livePerSessionStream === true` AND the picked unit IS a
 *     session → use the existing <LiveStreamPanel> (Anthropic MA proxy)
 *   - Explicit `livePerSessionStream === false` → use agent-filtered
 *     fleet stream regardless of unit kind
 *   - Capability undefined → INFER from unit kind. Session kind implies
 *     per-session SSE was historically the only thing that produced
 *     `ops_sessions` rows (Anthropic MA); job kind implies no per-session
 *     SSE. This keeps legacy agents working without requiring them to
 *     advertise the new flag.
 */
function pickLiveComponent(
  unit: WorkUnit | null,
  capabilities: DrilldownAgent['capabilities'],
): 'session-stream' | 'fleet-stream' {
  const flag = capabilities?.livePerSessionStream;
  if (flag === true && unit?.kind === 'session') return 'session-stream';
  if (flag === false) return 'fleet-stream';
  // Inference fallback (legacy agents): session kind → session stream,
  // job kind or no unit → fleet stream.
  if (unit?.kind === 'session') return 'session-stream';
  return 'fleet-stream';
}

function LiveSection({
  agentId,
  capabilities,
}: {
  agentId: string;
  capabilities: DrilldownAgent['capabilities'];
}) {
  const workUnits = useQuery({
    queryKey: ['drilldown', 'work-units', agentId],
    queryFn: () =>
      apiFetch<WorkUnitsResponse>(`/agents/${agentId}/work-units?limit=20`),
    enabled: !!agentId,
  });

  if (workUnits.isLoading) {
    return (
      <div className="h-24 animate-pulse rounded-md bg-neutral-800/60" />
    );
  }

  if (workUnits.isError) {
    return (
      <NoLiveStream
        reason={
          workUnits.error instanceof Error
            ? `Failed to load work units: ${workUnits.error.message}`
            : 'Failed to load work units for live stream.'
        }
      />
    );
  }

  const target = pickStreamUnit(workUnits.data?.data);
  const choice = pickLiveComponent(target, capabilities);

  if (choice === 'session-stream' && target?.sdkNativeId) {
    // Anthropic-MA path: subscribe to the per-session SSE proxy via the
    // session's native id (== ops_agent_sessions.id == Anthropic UUID).
    return <LiveStreamPanel sessionId={target.sdkNativeId} />;
  }

  // Runner / no-per-session-SSE path: agent-filtered org-wide fleet
  // stream. Works for any SDK that emits ops_agent_activity events.
  return <AgentFleetActivity agentId={agentId} />;
}

/* ── History section (paginated activity events) ────────────────────────── */

const HISTORY_PAGE_SIZE = 50;

function HistorySection({
  agentId,
  cursor,
  onLoadMore,
}: {
  agentId: string;
  cursor: string | null;
  onLoadMore: (next: string) => void;
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['drilldown', 'activity', agentId, cursor ?? 'head'],
    queryFn: () => {
      const params = new URLSearchParams({
        limit: String(HISTORY_PAGE_SIZE),
      });
      if (cursor) params.set('before', cursor);
      return apiFetch<ActivityResponse>(
        `/agents/${agentId}/activity?${params.toString()}`,
      );
    },
    enabled: !!agentId,
  });

  if (isLoading) return <HistorySkeleton />;

  if (isError) {
    return (
      <div className="rounded-md border border-red-800 bg-red-950/40 p-3 text-sm text-red-300">
        Failed to load history:{' '}
        {error instanceof Error ? error.message : 'unknown error'}
      </div>
    );
  }

  const events = data?.events ?? [];
  const nextCursor = data?.nextCursor ?? null;

  if (events.length === 0) {
    return (
      <div className="rounded-md border border-neutral-800 bg-neutral-900/50 p-4 text-xs text-neutral-400">
        No activity recorded for this agent yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <DataTable
        columns={eventColumns}
        data={events}
        emptyMessage="No activity recorded for this agent yet."
      />
      {nextCursor && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onLoadMore(nextCursor)}
          >
            Load older events
          </Button>
        </div>
      )}
    </div>
  );
}

/* ── Status label (lowercase agent status maps to the badge palette) ────── */

const STATUS_LABEL: Record<DrilldownAgent['status'], string> = {
  idle: 'idle',
  running: 'running',
  stopped: 'stopped',
  error: 'error',
};

/* ── Main drill-down sheet ──────────────────────────────────────────────── */

export function OperatorActivityDrilldown({
  agent,
  open,
  onClose,
}: {
  agent: DrilldownAgent | null;
  open: boolean;
  onClose: () => void;
}) {
  // History pagination cursor — null = head (most recent). Reset whenever
  // we switch to a different agent so we don't carry one agent's "before"
  // ISO timestamp across to another's stream.
  const [cursor, setCursor] = useState<string | null>(null);

  // Reset pagination when the target agent changes so the next agent's
  // history starts at the head, not the previous agent's deep page.
  useEffect(() => {
    setCursor(null);
  }, [agent?.agentId]);

  if (!agent) return null;

  const health = deriveAgentHealth({
    status: agent.status,
    lastHeartbeat: agent.lastHeartbeat,
  });

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        className="w-full overflow-y-auto sm:max-w-2xl"
        showCloseButton={false}
      >
        {/* Header — agent name, health, deep-link to full detail, close */}
        <div className="mt-2 flex items-start justify-between gap-4 border-b border-neutral-800 pb-4">
          <div className="min-w-0 flex-1">
            <h2 className="flex items-center gap-3 text-lg font-semibold text-neutral-100">
              <span className="truncate">{agent.name}</span>
              <AgentHealthBadge health={health} />
              <span className="text-xs uppercase text-neutral-500">
                {STATUS_LABEL[agent.status]}
              </span>
            </h2>
            <p className="mt-1 font-mono text-xs text-neutral-500">
              {agent.agentId}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Link to={`/agents/${agent.agentId}`} onClick={onClose}>
              <Button variant="outline" size="sm">
                Open full detail
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label="Close"
            >
              Close
            </Button>
          </div>
        </div>

        {/* Live section */}
        <section className="mt-4" aria-labelledby="drilldown-live-heading">
          <h3
            id="drilldown-live-heading"
            className="mb-2 text-sm font-medium text-neutral-300"
          >
            Live
          </h3>
          <LiveSection
            agentId={agent.agentId}
            capabilities={agent.capabilities}
          />
        </section>

        {/* History section */}
        <section
          className="mt-6 pb-6"
          aria-labelledby="drilldown-history-heading"
        >
          <h3
            id="drilldown-history-heading"
            className="mb-2 text-sm font-medium text-neutral-300"
          >
            History
          </h3>
          <HistorySection
            agentId={agent.agentId}
            cursor={cursor}
            onLoadMore={(next) => setCursor(next)}
          />
        </section>
      </SheetContent>
    </Sheet>
  );
}

/* ── Exports for unit tests ─────────────────────────────────────────────── */

// Exposed solely for the unit suite (drilldown.test.tsx). Not part of the
// public component API; do not import from app code.
export const __testing = {
  pickStreamUnit,
  pickLiveComponent,
  HISTORY_PAGE_SIZE,
};

// Re-export the ActivityEventKind type so callers don't need a separate
// import path for shared shapes.
export type { ActivityEventKind };
